"use client";

import { cn } from "@/lib/utils";

export type GlassVariant = "light" | "dark";
export type GlassBlur = "sm" | "md" | "lg";
export type GlassRounded = "none" | "sm" | "md" | "lg" | "xl" | "full";
export type GlassPadding = "none" | "sm" | "md" | "lg";

export interface GlassPanelProps {
  /** Light (white) or dark (black) glass effect */
  variant?: GlassVariant;
  /** Blur intensity */
  blur?: GlassBlur;
  /** Border radius */
  rounded?: GlassRounded;
  /** Padding */
  padding?: GlassPadding;
  /** Whether to show a subtle border */
  border?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Child elements */
  children?: React.ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Element type to render */
  as?: "div" | "button" | "span";
}

const variantClasses: Record<GlassVariant, string> = {
  light: "bg-white/20 hover:bg-white/30",
  dark: "bg-black/20 hover:bg-black/30",
};

const blurClasses: Record<GlassBlur, string> = {
  sm: "backdrop-blur-sm",
  md: "backdrop-blur-md",
  lg: "backdrop-blur-lg",
};

const roundedClasses: Record<GlassRounded, string> = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  full: "rounded-full",
};

const paddingClasses: Record<GlassPadding, string> = {
  none: "p-0",
  sm: "px-2 py-1",
  md: "px-3 py-2",
  lg: "px-4 py-3",
};

/**
 * GlassPanel - Frosted glass effect container
 *
 * Use this component to create glassmorphism effects on images or gradients.
 * Common use cases: badges on hero images, floating action buttons, overlays.
 *
 * @example
 * // Badge style
 * <GlassPanel variant="light" blur="sm" rounded="full" padding="sm">
 *   <span>Premium</span>
 * </GlassPanel>
 *
 * // Card overlay
 * <GlassPanel variant="dark" blur="md" rounded="lg" padding="lg">
 *   <p>Content on dark background</p>
 * </GlassPanel>
 */
export function GlassPanel({
  variant = "light",
  blur = "sm",
  rounded = "lg",
  padding = "md",
  border = false,
  className,
  children,
  onClick,
  as = "div",
}: GlassPanelProps) {
  const Component = as;

  return (
    <Component
      className={cn(
        "transition-colors duration-200",
        variantClasses[variant],
        blurClasses[blur],
        roundedClasses[rounded],
        paddingClasses[padding],
        border && "border border-white/30",
        onClick && "cursor-pointer",
        className
      )}
      onClick={onClick}
    >
      {children}
    </Component>
  );
}

export default GlassPanel;
